import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../i18n/format';
import { useAppSelector } from '../app/hooks';
import Button from './Button';
import TextInput from './TextInput';
import styles from '../sass/components/Invoice.module.scss';

interface CreditInputProps {
  availableCredit: number;
  appliedCredit: number | null;
  setAppliedCredit: (amount: number) => void;
  errorMessage?: string;
  setErrorMessage: (msg: string) => void;
}

export default function CreditInput({
  availableCredit,
  appliedCredit,
  setAppliedCredit,
  errorMessage,
  setErrorMessage,
}: CreditInputProps) {
  const { t } = useTranslation();
  const currentUser = useAppSelector((
    state,
  ) => state.auth.currentUser);
  const userRoles = currentUser?.roles ?? [];
  const isStaff = ['manager', 'tester', 'technician']
    .some((r) => userRoles.includes(r));
  const [inputValue, setInputValue] = useState(
    appliedCredit != null ? (appliedCredit / 100).toString() : '',
  );
  useEffect(() => {
    setInputValue(
      appliedCredit != null ? (appliedCredit / 100).toString() : '',
    );
  }, [appliedCredit]);

  const handleApplyCredit = () => {
    const parsed = parseFloat(inputValue);
    if (Number.isNaN(parsed) || parsed < 0) {
      setErrorMessage(t('credit.enterValid'));
      return;
    }
    const cents = Math.round(parsed * 100);
    if (cents > availableCredit) {
      setErrorMessage(t('credit.max', {
        amount: formatMoney(availableCredit / 100),
      }));
      return;
    }

    setAppliedCredit(cents);
    setErrorMessage('');
  };

  return (
    <div key="apply-credit">
      <span className="label">{t('credit.label')}</span>
      <div className={styles.cardContent}>
        <div className={styles.availableCreditInfo}>
          {isStaff
            ? `Customer have ${formatMoney(availableCredit / 100)} in referral`
              + ' credit available.'
            : t('credit.available', {
              amount: formatMoney(availableCredit / 100),
            })}
        </div>
        <div className={styles.discountInputWrapper}>
          <TextInput
            value={inputValue}
            onChange={(e) => setInputValue(e.currentTarget.value)}
            className={styles.discountInput}
            placeholder={t('credit.placeholder')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleApplyCredit();
              }
            }}
          />
          <Button
            className={styles.applyDiscountButton}
            onClick={handleApplyCredit}
          >
            {t('discount.apply')}
          </Button>
        </div>
        {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
      </div>
    </div>
  );
}
